import { type PipelineMessageFilter } from '@post-office/pipeline/filters';
import { type SerializableRecord } from '@post-office/serializable-record';
import { FilterResult, type FilterResponse } from '@post-office/shared-contracts/message-filters';
import type { ActiveScopes } from '@post-office/shared-contracts/util';
import type { RequestContext } from '@post-office/shared-contracts/shared-request-middleware-context';
import type {
	DataRetrievalMessageToFilter,
	DataRetrievalFilter,
	SharedFilterParams,
} from '@post-office/shared-contracts/data-retrieval-filters';
import type { MessageToFilter } from '@post-office/shared-contracts/types';
import type { Recipient } from '@post-office/shared-contracts/recipients';
import type { EligibleMessage } from '@post-office/universal-contracts/shared-request-middleware-context/context-types';

import { coreActiveScopesCheck } from './core-active-scopes-check';

export type Scopes = Array<{ name: string }>;

/**
 * This is currently unused as the `message` does not match up.
 * post-office/services/post-office/src/orchestration/service/implicit-message-processor.ts
 *
 * Needs to be updated to support sending down `scopes`, however there is currently no where to attach those.
 * I have opted to keep this checked in source as it is still a useful reference for the future.
 */
export async function createActiveScopesCheckDataRetrievalFilter(
	createFilterParams: {
		activeScopes: ActiveScopes | undefined;
	},
	sharedFilterParams: SharedFilterParams,
): Promise<DataRetrievalFilter> {
	const { activeScopes } = createFilterParams;

	return function executeFilter(message: DataRetrievalMessageToFilter): FilterResponse {
		// @ts-expect-error - TODO: Update post-office/services/post-office/src/orchestration/service/implicit-message-processor.ts
		return activeScopeCheckFilter(activeScopes, message, sharedFilterParams);
	};
}

/**
 * Creates a pipeline message filter that checks whether each message's scopes
 * intersect with the currently active scopes.
 *
 * Uses `coreActiveScopesCheck` — the same algorithm as `checkActiveScopes` used
 * by implicit-message authors. This guarantees that the filter stage and the
 * implicit-message creation stage always produce the same active/deny decision.
 *
 * Now also used as the canonical implementation for the screen-space-flags placement
 * filter (`integrated-teams/marketing/placements/screen-space-flags/src/filters/index.ts`).
 */
export const createActiveScopesCheckFilter = (
	requestContext: RequestContext,
	{
		activeScopes,
	}: {
		activeScopes?: ActiveScopes;
	},
): PipelineMessageFilter<SerializableRecord> => ({
	execute: async (message, options) => {
		return activeScopeCheckFilter(activeScopes, message, {
			requestContext: requestContext,
			...options.deps,
		});
	},
});

const activeScopeCheckFilter = (
	activeScopes: ActiveScopes | undefined,
	message: MessageToFilter<SerializableRecord, Recipient>,
	sharedFilterParams: SharedFilterParams,
): FilterResponse => {
	const { logger } = sharedFilterParams;

	// Skip filter for non-implicit messages — not part of the core scope check.
	if (message.messageCreationType !== 'implicit') {
		return { result: FilterResult.Skip, filterName: 'activeScopesCheckFilter' };
	}

	// Message-specific guard: the message must declare its required scopes.
	if (!Array.isArray(message.context.scopes) || message.context.scopes.length === 0) {
		return {
			result: FilterResult.Deny,
			failureReason: `No scopes found for the message with messageInstanceId: ${message.messageInstanceId} and templateId: ${message.messageTemplateId}`,
			filterName: 'activeScopesCheckFilter',
		};
	}

	const messageScopes: Scopes = message.context.scopes as Scopes;

	const result = coreActiveScopesCheck({
		activeScopes,
		requiredScopes: messageScopes,
		request: { logger },
	});

	switch (result.outcome) {
		case 'active':
			return { result: FilterResult.Allow, filterName: 'activeScopesCheckFilter' };

		case 'no-active-scopes':
			return {
				result: FilterResult.Deny,
				failureReason:
					'Active scopes was not found in additional filter context. Make sure the placement is providing the activeScopes',
				filterName: 'activeScopesCheckFilter',
			};

		case 'excluded-scope':
			return {
				result: FilterResult.Deny,
				failureReason: `Active scope matches excluded route: ${result.excludedScopes.join(', ')}`,
				filterName: 'activeScopesCheckFilter',
			};

		case 'no-required-scopes':
			return {
				result: FilterResult.Deny,
				failureReason: `No scopes found for the message with messageInstanceId: ${message.messageInstanceId} and templateId: ${message.messageTemplateId}`,
				filterName: 'activeScopesCheckFilter',
			};

		case 'no-match':
			return {
				result: FilterResult.Deny,
				failureReason: `The following scopes are not active: ${result.nonIntersectedScopes.join(', ')}`,
				filterName: 'activeScopesCheckFilter',
			};

		default: {
			// Exhaustiveness guard — if CoreActiveScopesCheckResult gains a new member,
			// the explicit return type above will surface a compile error here.
			const _exhaustive: never = result;
			_exhaustive;
			return {
				result: FilterResult.Deny,
				failureReason: 'Unhandled scope check outcome',
				filterName: 'activeScopesCheckFilter',
			};
		}
	}
};

/**
 * Filters an array of `EligibleMessage` objects using the same `coreActiveScopesCheck`
 * algorithm as `checkActiveScopes` and `createActiveScopesCheckFilter`.
 *
 * This is the in-app-v2 equivalent of `createActiveScopesCheckFilter` for use in
 * placement `preInit` stages, where messages are `EligibleMessage` (a stripped-down
 * subset of `MessageToOrchestrate` returned by `getEligibleMessages`). The
 * `PipelineMessageFilter` interface targets the fuller `MessageToFilter` type which
 * includes fields not present on `EligibleMessage` (e.g. `messageTemplate`,
 * `messageConfig`, `recipient`), so it cannot be used directly in `preInit`.
 */
export const filterEligibleMessagesByActiveScopes = <T extends SerializableRecord>(
	messages: EligibleMessage<T>[],
	{
		activeScopes,
		requestContext,
	}: {
		activeScopes: ActiveScopes | undefined;
		requestContext: RequestContext;
	},
): EligibleMessage<T>[] => {
	const { logger } = requestContext;

	return messages.filter((message) => {
		// Message-specific guard: the message must declare its required scopes.
		if (!Array.isArray(message.context.scopes) || message.context.scopes.length === 0) {
			logger.warn(
				{
					messageInstanceId: message.messageInstanceId,
					messageTemplateId: message.messageTemplateId,
				},
				'activeScopesCheckFilter: denying message — no scopes defined on message',
			);
			return false;
		}

		const messageScopes: Scopes = message.context.scopes as Scopes;

		const result = coreActiveScopesCheck({
			activeScopes,
			requiredScopes: messageScopes,
			request: { logger },
		});

		return result.outcome === 'active';
	});
};
